import React, { forwardRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { transparentize } from 'polished';
import classNames from 'classnames';

import {
    Sizes,
    Colours,
    Typography,
    IconChooser,
    getOrderedColour,
} from '@ratehub/base-ui';
import { Spacing, ZIndex } from '@ratehub/base-ui/src/styles';

import { BREAKPOINT_LARGE, STORE_FRONT_VARIANTS } from './StoreFrontContainer';

// Map stroke colours from getOrderedColour() to background colours (for icons).
// eg. [stroke colour]: fill colour
const THEME_COLOUR_MAP = {
    [Colours.BLUEBERRY_DARK]: Colours.BLUEBERRY_LIGHTEST,
    [Colours.LIME_DARK]: Colours.MINT_LIGHT,
    [Colours.YUZU_DARKEST]: Colours.YUZU_LIGHTEST,
    [Colours.STRAWBERRY]: Colours.WATERMELON_LIGHTEST,
    [Colours.TANGERINE_DARK]: Colours.TANGERINE_LIGHTEST,
    [Colours.GRAPE_DARK]: Colours.GRAPE_LIGHTEST,
    [Colours.WATERMELON]: Colours.WATERMELON_LIGHTEST,
};

const MAX_VIEWPORT_WIDTH = 120; // rem
const MIN_FONT_SIZE = Sizes.FONTS.M;    // 1.125rem
const MAX_FONT_SIZE = Sizes.FONTS.XL;   // 1.5rem
// parseFloat() automatically ignores the units at the end of Sizes.FONTS sizes
const MAX_FONT_SIZE_INCREASE = parseFloat(Sizes.FONTS.XL) - parseFloat(Sizes.FONTS.M);
const MAX_AVAILABLE_VIEWPORT_WIDTH = MAX_VIEWPORT_WIDTH - parseFloat(BREAKPOINT_LARGE);

// Ref is needed for WordPress context
const StoreFrontItem = forwardRef((props, ref) => {
    const {
        title,
        url,
        iconKey,
        styleIndex = 0,
        variant = STORE_FRONT_VARIANTS.standard,
        className,
        ...otherProps
    } = props;

    // convenient way to reference variant through booleans (yes, would get awkward if we had more than two)
    const isStandard = variant === STORE_FRONT_VARIANTS.standard;
    const isCondensed = variant === STORE_FRONT_VARIANTS.condensed;

    return (
        <Container
            className={classNames('store-front-item', className, {
                'is-standard rh-mb-1': isStandard,
                'is-condensed': isCondensed,
            })}
            ref={ref}
            {...otherProps}
        >
            <a
                className={classNames(
                    'anchor rh-position-relative rh-display-flex rh-height-100p',
                    'rh-fg-blackberry rh-bg-coconut rh-outline-none rh-box-sizing-border-box',
                    'hover--rh-fg-blackberry active--rh-fg-blackberry focus--rh-fg-blackberry', {
                        'with-icon': !!iconKey,
                        'without-icon': !iconKey,
                    })}
                href={url}
                data-name="storeFrontItem-visitItemLink"
            >
                <IconChooser
                    className="icon rh-ml-1 rh-mr-0_5 rh-display-block rh-icon-m"
                    iconKey={iconKey}
                    stroke={getStroke(styleIndex)}
                    strokeWidth="1.5px"
                    fill={getFill(styleIndex)}
                    outlineWidth="0"
                    size="L"
                />

                <p className="label rh-pr-0_5 rh-pl-0_5 rh-text-xl leading-s rh-ml-0 rh-text-wrap-balance">
                    {title}
                </p>
            </a>
        </Container>
    );
});

function getStroke(styleIndex) {
    return getOrderedColour(styleIndex);
}

function getFill(styleIndex) {
    return THEME_COLOUR_MAP[getOrderedColour(styleIndex)];
}


StoreFrontItem.propTypes = {
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    iconKey: PropTypes.string.isRequired,
    styleIndex: PropTypes.number,
    variant: PropTypes.oneOf(Object.values(STORE_FRONT_VARIANTS)),
    className: PropTypes.string,
};

const EASING_CURVE = 'cubic-bezier(0.19, 1, 0.22, 1)';

const Container = styled.li`
    > .anchor {
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        text-decoration: none;

        /* Including the shadow in its own ::before allows us to apply a
        mix-blend-mode to JUST the shadow WITHOUT need to apply that blend
        mode to all of this element's children. */
        &::before {
            content: '';
            position: absolute;
            top: 0;
            right: 0;
            left: 0;
            bottom: 0;

            box-shadow: 0 2px 8px 0 ${transparentize(0.3, Colours.STONE)};
            mix-blend-mode: multiply;

            z-index: 0;
        }

        backface-visibility: hidden;
        transition: transform 0.4s ${EASING_CURVE},
                    box-shadow 0.4s ${EASING_CURVE},
                    background-color 0.4s ${EASING_CURVE};

        &:hover,
        &:focus,
        &:active {
            z-index: ${ZIndex.ELEMENTS};
        }

        > .icon {
            flex-shrink: 0;
        }
    }

    &.is-standard {
        > .anchor {
            border-radius: ${Spacing.HALF};
            &::before {
                border-radius: ${Spacing.HALF};
            }

            &:hover,
            &:focus {
                /* Note: transforms break the ::before's ability to blend with
                backgrounds below the Store Front. Truly maddening, but considered
                an acceptable loss since it happens on hover only */
                transform: scale(1.1);

                box-shadow: 0 4px 10px 0 ${Colours.STONE};
            }

            &:active {
                background-color: ${Colours.STONE_LIGHTEST};
                transform: scale(1.05);

                box-shadow: 0 3px 7.5px 0 ${Colours.STONE};
            }
        }
    }

    &.is-condensed {
        > .anchor {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            border-radius: ${Spacing.QUARTER};

            &::before {
                border-radius: ${Spacing.QUARTER};
            }

             &:hover,
             &:focus {
                 background-color: ${Colours.BLUEBERRY_LIGHTEST};
             }

            &.with-icon {
                > .icon {
                    margin-bottom: 0;
                }
            }

            // tablet and smaller
            @media screen and (max-width: ${BREAKPOINT_LARGE}) {
                > .label {
                    margin-top: ${Spacing.ONE};   // browser default, but let’s be explicit
                    margin-bottom: ${Spacing.ONE};
                }

                &.without-icon {
                    justify-content: center;

                    > .label {
                        text-align: center;
                    }
                }

                &.with-icon {
                    > .label {
                        font-size: ${Sizes.FONTS.S};
                    }
                }
            }
        }
    }


    // 2 rows (desktop) (> 60rem)
    @media screen and (min-width: ${BREAKPOINT_LARGE}) {
        > .anchor {
            flex-direction: column;

            > .icon {
                margin-bottom: ${Spacing.HALF};
                // centres icon within button
                margin-left: auto;
                margin-right: auto;
            }

            > .label {
                margin: 0 auto;
                text-align: center;
            }
        }

        &.is-standard {
            > .anchor {
                padding-top: ${Spacing.ONE_AND_A_HALF};
                padding-bottom: ${Spacing.ONE_AND_A_HALF};

                > .label {
                    // using our existing values for these variables, be aware that the middle arg to clamp()
                    // correctly simplifies to font-size: calc(0.75rem + 0.625vw);
                    font-size: clamp(
                        ${MIN_FONT_SIZE},
                        calc(${MIN_FONT_SIZE} + ${MAX_FONT_SIZE_INCREASE} * (100vw - ${BREAKPOINT_LARGE}) / ${MAX_AVAILABLE_VIEWPORT_WIDTH}),
                        ${MAX_FONT_SIZE}
                    );
                }
            }
        }

        &.is-condensed {
            > .anchor {
                padding-top: ${Spacing.THREE_QUARTERS};
                padding-bottom: ${Spacing.THREE_QUARTERS};

                > .label {
                    margin-top: ${Spacing.HALF};
                    margin-bottom: ${Spacing.HALF};
                }
            }
        }
    }
`;


StoreFrontItem.blockKey = 'rh/store-front-item';

export default StoreFrontItem;
